import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import { maxBy, memoize, intersectionBy, values, flatten } from 'lodash';
import SemVer from 'semver';

import { buildsActionTypes } from './../actions/types';
import { utils, build as constants } from './../helpers';
import { cachedRequestByIdReducer } from './helpers';
import * as jobBuildsReducer from './jobBuilds';


const builds = cachedRequestByIdReducer(buildsActionTypes);
export default builds;

const getBuildState = state => state.builds;
export const buildsById = state => getBuildState(state).byId;
export const getBuildById = (state, id) => buildsById(state)[id];

export const getBuilds = createSelector(
  getBuildState,
  (builds) => values(builds.byId)
);

const byProduct = builds => utils.propertyAccumulatorReducer(builds, 'product');
const buildsByProducts = createSelector(
  getBuilds,
  (builds) => byProduct(builds)
);

export const buildsByBuildTypes = createSelector(
  getBuilds,
  (builds) => builds.reduce((result, b) => {
    const buildTypes = constants.buildTypesMapper(b);
    if (buildTypes.length === 0) {
      return result;
    }

    return buildTypes.reduce((acc, bt) => {
      const cur = acc[bt] ? acc[bt] : [];
      return {
        ...acc,
        [bt]: [
          ...cur,
          b
        ]
      };
    }, result);
  }, {})
);

export const getBuildsByTypeAndBuildNumber = createSelector(
  buildsByBuildTypes,
  (buildsByType) => memoize(({ buildType, buildNumber }) => {
    if (!buildType || !buildNumber) return null;
    const builds = buildsByType[buildType] || [];
    return builds.filter(b => b.build === buildNumber);
  })
);

const latestBuildByProduct = buildsByProducts =>
  values(buildsByProducts).reduce((result, builds) => {
    if (builds.length > 0) {
      const latestRelease = maxBy(builds, b => b.build);
      result[latestRelease.product] = latestRelease;
    }

    return result;
  }, {}
  );

export const buildsByTypesAndProducts = createSelector(
  buildsByBuildTypes,
  (buildsByBuildTypes) => Object.keys(buildsByBuildTypes).reduce((result, buildType) => {
    const builds = buildsByBuildTypes[buildType];
    const buildsByProducts = byProduct(builds);
    result[buildType] = buildsByProducts;
    return result;
  }, {})
);

export const latestReleaseBuildByProducts = createSelector(
  buildsByTypesAndProducts,
  (buildsByTypesAndProducts) => {
    if (buildsByTypesAndProducts[constants.LATEST_RELEASE]) {
      return latestBuildByProduct(
        buildsByTypesAndProducts[constants.LATEST_RELEASE]);
    }
    return undefined;
  }
);

export const releaseBuildsPresentForProducts = createSelector(
  latestReleaseBuildByProducts,
  (latestReleaseBuildByProducts) => {
    return !!(latestReleaseBuildByProducts &&
      latestReleaseBuildByProducts[constants.PRODUCTS.IEF_ENGINE] &&
      latestReleaseBuildByProducts[constants.PRODUCTS.PROCESS] &&
      latestReleaseBuildByProducts[constants.PRODUCTS.EXAMINE]);
  }
);

export const filterBuildsByProduct = (buildsByProducts, product) => {
  const actualProducts = constants.PRODUCTS_BY_PROD[product] || [product];
  return flatten(actualProducts.map(ap => buildsByProducts[ap]).filter(x => x));
};

export const filterBuilds = createSelector(
  getBuilds,
  buildsByBuildTypes,
  buildsByProducts,
  (allBuilds, buildsByBuildTypes, buildsByProducts) => memoize(
    ({ buildType, product, buildNumber }) => {
      const noFilters = !buildType && !product;
      const filteredBuilds = [];

      if (buildType) {
        filteredBuilds.push(buildsByBuildTypes[buildType]);
      }
      if (product) {
        const bs = filterBuildsByProduct(buildsByProducts, product);
        filteredBuilds.push(bs);
      }

      const builds = noFilters ?
        allBuilds :
        intersectionBy(...filteredBuilds, b => b.id);

      if (!buildNumber) {
        return builds;
      }

      return builds.filter(b => b.build === buildNumber);
    }
  )
);

export const prodBuildsVersion = createSelector(
  getBuilds,
  (builds) => values(builds).reduce((result, build) => {
    build.is_prod ? result.push(build.version) : null;
    return result;
  }, [])
);

export const topicBuildsVersion = createSelector(
  getBuilds,
  (builds) => values(builds).reduce((result, build) => {
    build.is_topic ? result.push(build.version) : null;
    return result;
  }, [])
);

export const buildVersionsByTypesAndProducts = createSelector(
  buildsByTypesAndProducts,
  (builds) => memoize(({ type, product }) => {
    if (!(type && product)) { return []; }
    const buildsByType = builds && builds[type];
    return buildsByType ?
      buildsByType[product].map(b => b.version) :
      [];
  })
);

export const getPreviousBuildVersionFromJobByProduct = createCachedSelector(
  (state, jobId) => jobBuildsReducer.getBuildIdsByJobId(state, jobId),
  (state) => state,
  (jobBuilds, state) => {
    if (!jobBuilds) return {};

    // return latest version for each product's build_id
    return jobBuilds.reduce((result, id) => {
      const build = getBuildById(state, id);
      if (!build) return {};

      let type = constants.buildTypesMapper(build)[0];
      type = type === constants.BUILD_RC ? constants.BUILD_PRODUCTION : type;
      const relevantBuilds = buildsByTypesAndProducts(state)[type][build.product];

      const currSemVer = SemVer.coerce(build.version).raw;

      const prevBuild = values(relevantBuilds).reduce((max, candidate) => {
        const candSemVer = SemVer.coerce(candidate.version).raw;

        // if candidate major.minor.patch is gt current verion, skip
        if (SemVer.gt(candSemVer, currSemVer)) { return max; }

        const maxSemVer = SemVer.coerce(max.semVer).raw;

        // if candidate major.minor.patch is gt max, new max
        if (SemVer.gt(candSemVer, maxSemVer) && candidate.version !== build.version) {
          max = { semVer: candSemVer, buildNum: candidate.build };
        }

        // if candidate major.minor.patch is eq max, check prelease versions
        if (SemVer.eq(candSemVer, maxSemVer)) {
          if (candidate.build > max.buildNum && candidate.build < build.build) {
            max = { semVer: candSemVer, buildNum: candidate.build };
          }
        }

        return max;
      }, { semVer: '0', buildNum: '0' });

      result[build.product] = `${prevBuild.semVer}.${prevBuild.buildNum}`;
      return result;
    }, {});
  }
)(
  (_state, jobId) => jobId
);


// WEBPACK FOOTER //
// src/reducers/builds.js